import { SubscriptionStatusEnum } from './subscription-status.enum';

/** The backend's EnumResource. */
export interface EnumValue<T extends string = string> {
  value: T;
  label: string;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
}

export interface SubscriptionPrice {
  amount: string;
  currency: string;
  billing_period: EnumValue;
}

/** A queued plan change or renewal that will replace the subscription. */
export interface SubscriptionLiveSuccessor {
  /** null when the plan was deleted */
  plan: SubscriptionPlan | null;
  /** ISO-8601, midnight UTC */
  starts_at: string;
  awaiting_payment: boolean;
}

export interface PlanSubscription {
  status: EnumValue<SubscriptionStatusEnum>;
  /** ISO-8601, midnight UTC */
  starts_at: string;
  /** ISO-8601, midnight UTC */
  ends_at: string;
  auto_renew: boolean;
  /** The price this subscription was sold at has been withdrawn: it runs to ends_at and never renews. */
  not_renewable: boolean;
  /** null when the plan was deleted */
  plan: SubscriptionPlan | null;
  price: SubscriptionPrice;
  live_successor: SubscriptionLiveSuccessor | null;
  /** Only on an active subscription: a pending or scheduled one entitles the workspace to nothing yet. */
  limits?: PlanLimitRow[];
}

export interface PlanLimitRow {
  key: string;
  name: string;
  unit: string;
  /** null = unlimited */
  limit: number | null;
  usage: number;
}

/** GET dashboard/plan: `data` is the subscription, and is absent when the workspace has none. */
export interface PlanUsageResponse {
  data?: PlanSubscription;
}

/** A limit row with its bar fill and at-limit flag precomputed for the template. */
export interface PlanLimitView extends PlanLimitRow {
  ratio: number;
  atLimit: boolean;
}

/** 0..1 bar fill; a zero limit with any usage reads as full. */
export function usageRatio(row: PlanLimitRow): number {
  if (row.limit === null) return 0;
  if (row.limit === 0) return row.usage > 0 ? 1 : 0;
  return Math.min(1, row.usage / row.limit);
}

export function atLimit(row: PlanLimitRow): boolean {
  return row.limit !== null && row.usage >= row.limit;
}
